import { useTheme } from "@/theme";

/**
 * Семантические классы оформления для страниц интеграций.
 *
 * Компоненты не дублируются под две темы: разметка одна, а цвета берутся
 * отсюда. Меняется контент — меняется сразу в обеих темах, вёрстка не
 * расползается. Токены сгруппированы по смыслу, не по цвету.
 */
export interface Surfaces {
  page: string;
  sectionAlt: string;
  divider: string;

  /* Текст */
  h1: string;
  h2: string;
  h3: string;
  body: string;
  muted: string;
  faint: string;
  accent: string;

  /* Хлебные крошки */
  crumbLink: string;
  crumbSep: string;
  crumbCurrent: string;

  /* Бейджи и чипы */
  badge: string;
  badgeIcon: string;
  chip: string;
  chipCount: string;

  /* Кнопки */
  ctaPrimary: string;
  ctaSecondary: string;

  /* Карточки */
  card: string;
  cardIconBox: string;
  cardIcon: string;
  cardPrice: string;
  cardTitle: string;
  cardText: string;
  cardLink: string;

  /* Блоки категорий */
  categoryIconBox: string;
  categoryIcon: string;

  /* FAQ */
  faqItemOpen: string;
  faqItemClosed: string;
  faqQuestionOpen: string;
  faqQuestionClosed: string;
  faqToggleOpen: string;
  faqToggleClosed: string;
  faqToggleIconOpen: string;
  faqToggleIconClosed: string;

  /* Мелочи */
  bulletCircle: string;
  bulletIcon: string;
  bulletText: string;
  contactLink: string;
  glowA: string;
  glowB: string;
  glowC: string;

  /* Детальная страница */
  panel: string;
  panelTitle: string;
  checkCircle: string;
  checkIcon: string;
  industryChip: string;
  relatedChip: string;
  neighborCard: string;
  neighborLabel: string;
  neighborTitle: string;
  othersChip: string;
  othersAll: string;
  metaCard: string;
  metaText: string;
}

const light: Surfaces = {
  page: "min-h-screen bg-[#FAFAF8] font-sans",
  sectionAlt: "bg-white border-y border-stone-100",
  divider: "border-stone-200",

  h1: "text-stone-900",
  h2: "text-stone-900",
  h3: "text-stone-900",
  body: "text-stone-600",
  muted: "text-stone-500",
  faint: "text-stone-400",
  accent: "text-orange-700",

  crumbLink: "text-stone-600 hover:text-stone-900",
  crumbSep: "text-stone-400",
  crumbCurrent: "text-stone-700",

  badge: "bg-stone-100 border border-stone-200 text-stone-600",
  badgeIcon: "bg-emerald-500",
  chip: "bg-white border border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-700 hover:shadow-sm",
  chipCount: "text-stone-500",

  ctaPrimary: "bg-stone-900 text-white hover:bg-stone-800",
  ctaSecondary:
    "border border-stone-300 text-stone-700 hover:border-stone-400 hover:bg-white",

  card: "bg-white border border-stone-200 hover:border-orange-300 hover:shadow-[0_8px_30px_rgba(0,0,0,0.06)]",
  cardIconBox: "bg-stone-100 group-hover:bg-orange-50",
  cardIcon: "text-stone-700 group-hover:text-orange-700",
  cardPrice: "text-stone-500",
  cardTitle: "text-stone-900",
  cardText: "text-stone-600",
  cardLink: "text-orange-700",

  categoryIconBox: "bg-stone-900",
  categoryIcon: "text-white",

  faqItemOpen: "border-stone-300 bg-stone-50",
  faqItemClosed: "border-stone-200 bg-white",
  faqQuestionOpen: "text-stone-900",
  faqQuestionClosed: "text-stone-700",
  faqToggleOpen: "bg-stone-900 rotate-45",
  faqToggleClosed: "bg-stone-100",
  faqToggleIconOpen: "text-white",
  faqToggleIconClosed: "text-stone-500",

  bulletCircle: "bg-white border border-stone-200",
  bulletIcon: "text-stone-600",
  bulletText: "text-stone-600",
  contactLink: "text-stone-600 hover:text-stone-900",
  glowA: "bg-orange-200/25",
  glowB: "bg-amber-200/25",
  glowC: "bg-emerald-200/20",

  panel: "bg-white border border-stone-200",
  panelTitle: "text-stone-900",
  checkCircle: "bg-emerald-50",
  checkIcon: "text-emerald-600",
  industryChip: "bg-stone-100 text-stone-600",
  relatedChip:
    "bg-stone-50 border border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-700 hover:bg-orange-50",
  neighborCard: "bg-white border border-stone-200 hover:border-orange-300 hover:shadow-sm",
  neighborLabel: "text-stone-600",
  neighborTitle: "text-stone-900 group-hover:text-orange-700",
  othersChip:
    "bg-white border border-stone-200 text-stone-600 hover:border-orange-300 hover:text-orange-700",
  othersAll: "bg-stone-900 text-white hover:bg-stone-800",
  metaCard: "bg-white border border-stone-200",
  metaText: "text-stone-600",
};

const dark: Surfaces = {
  page: "min-h-screen bg-[#0A0A0A] font-sans",
  sectionAlt: "bg-[#0C0C0E] border-y border-white/[0.06]",
  divider: "border-white/[0.08]",

  h1: "text-white",
  h2: "text-white",
  h3: "text-white",
  body: "text-white/65",
  muted: "text-white/55",
  faint: "text-white/50",
  accent: "text-orange-400",

  crumbLink: "text-white/55 hover:text-white",
  crumbSep: "text-white/35",
  crumbCurrent: "text-white/80",

  badge: "bg-white/[0.06] border border-white/[0.10] text-white/70",
  badgeIcon: "bg-emerald-400",
  chip: "bg-white/[0.04] border border-white/[0.10] text-white/70 hover:border-orange-400/40 hover:text-orange-300",
  chipCount: "text-white/45",

  ctaPrimary: "bg-orange-500 text-[#0A0A0A] hover:bg-orange-400",
  ctaSecondary: "border border-white/20 text-white hover:bg-white/[0.06]",

  card: "bg-white/[0.04] border border-white/[0.09] hover:border-orange-400/40 hover:bg-white/[0.06]",
  cardIconBox: "bg-white/[0.06] group-hover:bg-orange-500/[0.15]",
  cardIcon: "text-white/70 group-hover:text-orange-300",
  cardPrice: "text-white/55",
  cardTitle: "text-white",
  cardText: "text-white/65",
  cardLink: "text-orange-400",

  categoryIconBox: "bg-orange-500/[0.15]",
  categoryIcon: "text-orange-400",

  faqItemOpen: "border-orange-400/30 bg-white/[0.05]",
  faqItemClosed: "border-white/[0.09] bg-white/[0.03]",
  faqQuestionOpen: "text-white",
  faqQuestionClosed: "text-white/80",
  faqToggleOpen: "bg-orange-500 rotate-45",
  faqToggleClosed: "bg-white/[0.08]",
  faqToggleIconOpen: "text-[#0A0A0A]",
  faqToggleIconClosed: "text-white/60",

  bulletCircle: "bg-white/[0.06] border border-white/[0.10]",
  bulletIcon: "text-white/70",
  bulletText: "text-white/65",
  contactLink: "text-white/55 hover:text-white",
  glowA: "bg-orange-500/12",
  glowB: "bg-amber-500/10",
  glowC: "bg-emerald-500/08",

  panel: "bg-white/[0.04] border border-white/[0.09]",
  panelTitle: "text-white",
  checkCircle: "bg-emerald-500/[0.15]",
  checkIcon: "text-emerald-400",
  industryChip: "bg-white/[0.06] text-white/65",
  relatedChip:
    "bg-white/[0.04] border border-white/[0.10] text-white/70 hover:border-orange-400/40 hover:text-orange-300",
  neighborCard: "bg-white/[0.04] border border-white/[0.09] hover:border-orange-400/40",
  neighborLabel: "text-white/45",
  neighborTitle: "text-white group-hover:text-orange-300",
  othersChip:
    "bg-white/[0.04] border border-white/[0.10] text-white/70 hover:border-orange-400/40 hover:text-orange-300",
  othersAll: "bg-orange-500 text-[#0A0A0A] hover:bg-orange-400",
  metaCard: "bg-white/[0.04] border border-white/[0.09]",
  metaText: "text-white/65",
};

export function useSurfaces(): Surfaces {
  const { isDark } = useTheme();
  return isDark ? dark : light;
}

export const surfaces = { light, dark };
